/**
 * Global Command Palette (Cmd+K / Ctrl+K).
 *
 *  Iter 107 — base palette (navigation only)
 *  Iter 108 — RECENT destinations + ACTION commands (this revision):
 *    • Recent: every navigation made through the palette is stored in
 *      localStorage (latest 5). When the input is empty, RECENT is the
 *      first group rendered.
 *    • Actions: any row whose `kind === 'action'` runs a function instead
 *      of navigating. Typing a leading `>` filters the list to actions
 *      only (Linear / Notion convention).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { copyToClipboard } from '../lib/clipboard';
import {
  Search, Command as CommandIcon, ArrowRight, X,
  Building2, Users, TrendingUp, Trash2, LayoutDashboard,
  CreditCard, Settings, Calendar, FileText, Inbox, MapPin,
  BookOpen, ShieldCheck, GraduationCap, HeartPulse, BarChart3,
  Activity, ClipboardList, FlaskConical, Camera, Lock,
  LogOut, Globe, Copy, Plus, Receipt, Sparkles,
} from 'lucide-react';

const RECENT_KEY = 'cmdk_recent_v1';
const RECENT_MAX = 5;

/**
 * NAVIGATION rows. `kind` defaults to 'nav' — clicking navigates to `path`.
 * `roles[]` filters who can see each row.
 */
const ROUTES = [
  // Platform
  { path: '/platform',             label: 'Platform home',           icon: LayoutDashboard, category: 'Platform', roles: ['platform_admin'] },
  { path: '/platform/clinics',     label: 'Clinics',     hint: 'tenant directory', icon: Building2, category: 'Platform', roles: ['platform_admin'] },
  { path: '/platform/subscribers', label: 'Subscribers', hint: 'Stripe billing snapshot', icon: Users, category: 'Platform', roles: ['platform_admin'] },
  { path: '/platform/revenue',     label: 'Revenue',     hint: 'MRR, ARPU, churn cohort', icon: TrendingUp, category: 'Platform', roles: ['platform_admin'] },
  { path: '/platform/users',       label: 'Purge user',  hint: 'cascade-delete by email', icon: Trash2, category: 'Platform', roles: ['platform_admin'] },

  // Admin / clinic operations
  { path: '/admin',                label: 'Admin dashboard',         icon: LayoutDashboard, category: 'Admin', roles: ['admin', 'clinician'] },
  { path: '/admin/calendar',       label: 'Calendar',                icon: Calendar,        category: 'Admin', roles: ['admin', 'clinician', 'reception'] },
  { path: '/admin/users',          label: 'Users & invites',         icon: Users,           category: 'Admin', roles: ['admin'] },
  { path: '/admin/services',       label: 'Services & pricing',      icon: FileText,        category: 'Admin', roles: ['admin'] },
  { path: '/admin/locations',      label: 'Locations',               icon: MapPin,          category: 'Admin', roles: ['admin'] },
  { path: '/admin/finances',       label: 'Finances & invoices',     icon: FileText,        category: 'Admin', roles: ['admin'] },
  { path: '/admin/inbox',          label: 'GP referral inbox',       icon: Inbox,           category: 'Admin', roles: ['admin', 'clinician', 'reception'] },
  { path: '/admin/education',      label: 'Education library',       icon: GraduationCap,   category: 'Admin', roles: ['admin', 'clinician'] },
  { path: '/admin/billing',        label: 'Billing & subscription',  icon: CreditCard,      category: 'Admin', roles: ['admin'] },
  { path: '/admin/settings',       label: 'Clinic settings',         icon: Settings,        category: 'Admin', roles: ['admin'] },
  { path: '/admin/security',       label: 'Security & 2FA',          icon: ShieldCheck,     category: 'Admin', roles: ['admin', 'clinician'] },
  { path: '/admin/operations-manual', label: 'Operations manual',    icon: BookOpen,        category: 'Admin', roles: ['admin', 'clinician', 'reception', 'student'] },
  { path: '/admin/setup-manual',   label: 'Clinic setup manual',     icon: BookOpen,        category: 'Admin', roles: ['admin'] },

  // Patient
  { path: '/patient',              label: 'Patient dashboard',       icon: LayoutDashboard, category: 'Patient', roles: ['patient'] },
  { path: '/patient/book',         label: 'Book a session',          icon: Calendar,        category: 'Patient', roles: ['patient'] },
  { path: '/patient/appointments', label: 'My appointments',         icon: Calendar,        category: 'Patient', roles: ['patient'] },
  { path: '/patient/account',      label: 'Account & privacy',       icon: Lock,            category: 'Patient', roles: ['patient'] },
  { path: '/patient/education',    label: 'Watch & learn',           icon: GraduationCap,   category: 'Patient', roles: ['patient'] },
  { path: '/patient/hep',          label: 'My exercises',            icon: Activity,        category: 'Patient', roles: ['patient'] },
  { path: '/patient/outcomes',     label: 'Outcome measures',        icon: BarChart3,       category: 'Patient', roles: ['patient'] },
  { path: '/patient/surveys',      label: 'Recovery surveys',        icon: ClipboardList,   category: 'Patient', roles: ['patient'] },
  { path: '/patient/posture',      label: 'PostureCapture',          icon: Camera,          category: 'Patient', roles: ['patient'] },
  { path: '/patient/wellness',     label: 'Fitness & wellness',      icon: HeartPulse,      category: 'Patient', roles: ['patient'] },
  { path: '/patient/membership',   label: 'My membership',           icon: FlaskConical,    category: 'Patient', roles: ['patient'] },

  // Public / shared
  { path: '/help',                 label: 'Help center',             hint: 'FAQ + troubleshooting', icon: BookOpen,       category: 'Help', roles: ['*'] },
  { path: '/clinics',              label: 'Public clinic directory', icon: Building2,       category: 'Help', roles: ['*'] },
];

function recentLoad() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
  } catch (_) { return []; }
}
function recentPush(path) {
  try {
    const cur = recentLoad().filter(p => p !== path);
    cur.unshift(path);
    localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, RECENT_MAX)));
  } catch (_) { /* ignore */ }
}

/** Tiny scorer — higher is better, 0 means no match. */
function scoreRow(row, q) {
  if (!q) return 1;
  const needle = q.toLowerCase();
  const hay = (row.label + ' ' + (row.hint || '') + ' ' + row.category + ' ' + (row.path || '')).toLowerCase();
  if (row.label.toLowerCase().startsWith(needle)) return 100 + needle.length;
  if (hay.includes(needle)) return 50;
  const tokens = needle.split(/\s+/).filter(Boolean);
  if (tokens.every(t => hay.includes(t))) return 20;
  return 0;
}

function visibleFor(role, row) {
  if (!row.roles || row.roles.length === 0) return true;
  if (row.roles.includes('*')) return true;
  if (!role) return false;
  return row.roles.includes(role);
}

export default function CommandPalette({ user }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const [recent, setRecent] = useState(() => recentLoad());
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { logout, setLocale } = useAuth();

  // Build the actions list — depends on user role and the auth helpers.
  const ACTIONS = useMemo(() => {
    const list = [];
    if (user) {
      list.push({
        kind: 'action', key: 'logout', label: 'Log out',
        hint: 'sign out of this device', icon: LogOut, category: 'Actions',
        roles: ['*'], run: () => { logout(); },
      });
    }
    list.push(
      { kind: 'action', key: 'lang-en', label: 'Switch language → English',
        icon: Globe, category: 'Actions', roles: ['*'], run: () => setLocale('en') },
      { kind: 'action', key: 'lang-pl', label: 'Switch language → Polski',
        icon: Globe, category: 'Actions', roles: ['*'], run: () => setLocale('pl') },
      { kind: 'action', key: 'lang-es', label: 'Switch language → Español',
        icon: Globe, category: 'Actions', roles: ['*'], run: () => setLocale('es') },
      { kind: 'action', key: 'copy-url', label: 'Copy current URL',
        icon: Copy, category: 'Actions', roles: ['*'],
        run: () => copyToClipboard(window.location.href, { label: 'URL copied' }) },
    );
    // Role-specific shortcuts.
    if (['admin', 'clinician', 'reception'].includes(user?.role)) {
      list.push(
        { kind: 'action', key: 'new-appt', label: 'New appointment',
          hint: 'open calendar with new-event drawer', icon: Plus, category: 'Actions',
          roles: ['admin', 'clinician', 'reception'],
          run: () => navigate('/admin/calendar?new=1') },
      );
    }
    if (user?.role === 'admin') {
      list.push(
        { kind: 'action', key: 'new-invoice', label: 'New invoice',
          icon: Receipt, category: 'Actions', roles: ['admin'],
          run: () => navigate('/admin/finances?new=1') },
      );
    }
    if (user?.role === 'patient') {
      list.push(
        { kind: 'action', key: 'book-session', label: 'Book a session',
          icon: Sparkles, category: 'Actions', roles: ['patient'],
          run: () => navigate('/patient/book') },
      );
    }
    return list;
  }, [user, logout, setLocale, navigate]);

  // Global keybinding — ⌘K / Ctrl+K toggles open; Esc closes from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
        setQ(''); setIdx(0);
        setRecent(recentLoad());
      } else if (e.key === 'Escape') {
        setOpen(o => o ? false : o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); }, [open]);

  const role = user?.role;

  // Build the visible row list.
  const { rows, showRecentGroup, recentRows } = useMemo(() => {
    const trimmed = q.trim();
    // Leading-`>` → actions-only mode.
    if (trimmed.startsWith('>')) {
      const subq = trimmed.slice(1).trim();
      const matches = ACTIONS
        .filter(r => visibleFor(role, r))
        .map(r => ({ ...r, _score: scoreRow(r, subq) }))
        .filter(r => r._score > 0)
        .sort((a, b) => b._score - a._score);
      return { rows: matches, showRecentGroup: false, recentRows: [] };
    }
    // Normal: navigation + actions, both filterable.
    const all = [...ROUTES, ...ACTIONS]
      .filter(r => visibleFor(role, r))
      .map(r => ({ ...r, _score: scoreRow(r, trimmed) }))
      .filter(r => r._score > 0)
      .sort((a, b) => b._score - a._score)
      .slice(0, 40);

    // Recent group only when input is empty + we have any visible recents.
    const visibleRoutes = ROUTES.filter(r => visibleFor(role, r));
    const recents = trimmed
      ? []
      : recent.map(p => visibleRoutes.find(r => r.path === p)).filter(Boolean);
    return { rows: all, showRecentGroup: recents.length > 0, recentRows: recents };
  }, [q, role, recent, ACTIONS]);

  // Flatten so ↑↓ navigation can address every row including recents.
  const flatRows = useMemo(
    () => (showRecentGroup ? recentRows.map(r => ({ ...r, _recent: true })) : []).concat(rows),
    [showRecentGroup, recentRows, rows],
  );

  useEffect(() => { if (idx >= flatRows.length) setIdx(0); }, [flatRows, idx]);

  if (!open) return null;

  const go = (row) => {
    if (row.kind === 'action') {
      setOpen(false); setQ(''); setIdx(0);
      try { row.run(); } catch (_) { /* swallow */ }
      return;
    }
    setOpen(false); setQ(''); setIdx(0);
    recentPush(row.path);
    setRecent(recentLoad());
    navigate(row.path);
  };

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, flatRows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (flatRows[idx]) go(flatRows[idx]); }
  };

  // Group by category for readable rendering, with Recent first if present.
  const grouped = [];
  if (showRecentGroup) grouped.push(['Recent', recentRows.map(r => ({ ...r, _recent: true }))]);
  const byCat = rows.reduce((acc, r) => {
    (acc[r.category] = acc[r.category] || []).push(r);
    return acc;
  }, {});
  // Preserve insertion order roughly: Platform, Admin, Patient, Actions, Help.
  const order = ['Platform', 'Admin', 'Patient', 'Actions', 'Help'];
  order.forEach(cat => { if (byCat[cat]) grouped.push([cat, byCat[cat]]); });
  Object.keys(byCat).forEach(cat => {
    if (!order.includes(cat)) grouped.push([cat, byCat[cat]]);
  });

  let flatIdx = -1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] bg-black/40 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      data-testid="command-palette"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-xl bg-bg rounded-xl shadow-2xl border border-soft overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-soft">
          <Search className="w-5 h-5 text-fg-muted shrink-0"/>
          <input
            ref={inputRef}
            value={q}
            onChange={e => { setQ(e.target.value); setIdx(0); }}
            onKeyDown={onInputKey}
            placeholder="Jump to… (try '>' for actions)"
            className="flex-1 bg-transparent outline-none text-base placeholder:text-fg-muted"
            data-testid="command-palette-input"
            autoComplete="off"
          />
          <button onClick={() => setOpen(false)} className="text-fg-muted hover:text-fg" data-testid="command-palette-close">
            <X className="w-4 h-4"/>
          </button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto" data-testid="command-palette-list">
          {flatRows.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-fg-muted">
              Nothing matches <strong>&ldquo;{q}&rdquo;</strong>.
            </div>
          )}
          {grouped.map(([cat, items]) => (
            <div key={cat} data-testid={`command-palette-group-${cat.toLowerCase()}`}>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-fg-muted font-bold">
                {cat}
              </div>
              {items.map((r) => {
                flatIdx += 1;
                const active = flatIdx === idx;
                const Icon = r.icon;
                return (
                  <button
                    key={r._recent ? `recent-${r.path}` : (r.path || r.key)}
                    onMouseEnter={() => setIdx(flatIdx)}
                    onClick={() => go(r)}
                    data-testid={`command-palette-item-${(r.path || r.key).replace(/[^a-z0-9]/gi, '-')}`}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left ${
                      active ? 'bg-[#F2EBDB] text-[#003D80]' : 'text-fg hover:bg-fg/5'
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" strokeWidth={1.6}/>
                    <span className="flex-1 min-w-0">
                      <span className="font-medium">{r.label}</span>
                      {r.hint && <span className="text-fg-muted text-xs ml-2">· {r.hint}</span>}
                    </span>
                    {r.kind === 'action' ? (
                      <span className="text-[10px] uppercase tracking-wider text-brand">action</span>
                    ) : (
                      <span className="font-mono text-[10px] text-fg-muted">{r.path}</span>
                    )}
                    {active && <ArrowRight className="w-3 h-3 text-brand"/>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between px-4 py-2 border-t border-soft text-[11px] text-fg-muted">
          <div className="inline-flex items-center gap-2">
            <CommandIcon className="w-3 h-3"/>
            <span>↑↓ navigate · Enter open · Esc close · type &gt; for actions</span>
          </div>
          <div>{flatRows.length} result{flatRows.length === 1 ? '' : 's'}</div>
        </div>
      </div>
    </div>
  );
}
